import React, { useEffect, useState, useRef } from "react";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { db, auth, storage } from "../../Firebase_Config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./CreateBlog.css";

const CreateBlog = ({ authenticated, setAlertMsg, setAlertColor }) => {
  const hiddenFileInput = useRef(null);
  const [blogImg, setBlogImg] = useState(null); // { url, path, isLocal }
  const [title, setTitle] = useState("");
  const [postText, setPostText] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const postsCollectionRef = collection(db, "blogPosts");
  const navigate = useNavigate();

  // redirect if not logged in
  useEffect(() => {
    if (!authenticated) navigate("/Login");
  }, [authenticated, navigate]);

  // handle image selection & upload
  const handleImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setBlogImg({ url: localPreview, path: null, isLocal: true });

    const storageRef = ref(
      storage,
      `blogPosts/${auth.currentUser.uid}/${Date.now()}-${file.name}`
    );

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setBlogImg({
        url: downloadURL,
        path: storageRef.fullPath,
        isLocal: false,
      });
    } catch (error) {
      setAlertMsg("Image upload failed: " + error.message);
      setAlertColor("red");
      setBlogImg(null);
    }
  };

  const removeFile = async () => {
    if (!blogImg) return;
    try {
      if (blogImg.isLocal && blogImg.url) {
        URL.revokeObjectURL(blogImg.url);
      }
      if (blogImg.path) {
        await deleteObject(ref(storage, blogImg.path));
      }
      setBlogImg(null);
      if (hiddenFileInput.current) hiddenFileInput.current.value = null;
      setPreviewOpen(false); // close modal too
    } catch (error) {
      setAlertMsg(error.message);
      setAlertColor("red");
    }
  };

  // create new blog post
  const createBlogPost = async () => {
    if (!title.trim()) {
      setAlertMsg("Enter the Title of Your Blog!");
      setAlertColor("lightyellow");
      return;
    }
    if (!postText.trim()) {
      setAlertMsg("Enter Your Blog!");
      setAlertColor("lightyellow");
      return;
    }

    try {
      setLoading(true);
      await addDoc(postsCollectionRef, {
        title: title.trim(),
        postText: postText.trim(),
        ImgSrc: blogImg?.url || null,
        publishDate: Timestamp.fromDate(new Date()),
        status: "pending",
        author: {
          id: auth.currentUser?.uid,
          name: auth.currentUser?.displayName || "Anonymous",
          profileImg: auth.currentUser?.photoURL || null,
        },
        userId: auth.currentUser?.uid,
      });

      setAlertMsg("Your Blog has been sent for approval!");
      setAlertColor("lightblue");
      navigate("/MyBlogs");
    } catch (error) {
      setAlertMsg("Error creating blog: " + error.message);
      setAlertColor("red");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        <p>Uploading your blog...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Create Blog | VALSCO</title>
        <meta
          name="description"
          content="Create and submit your own blog post on Valsco Technology's platform."
        />
      </Helmet>

      <main className="create-blog-container">
        <div className="create-blog-card">
          <header className="create-blog-header">
            <h1>Write a New Blog</h1>
            <p>Share your thoughts and ideas with the community.</p>
          </header>

          <section className="form-section">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Blog Title"
              className="input-title"
            />

            <div className="image-upload">
              {!blogImg ? (
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => hiddenFileInput.current.click()}
                >
                  Upload Image
                </button>
              ) : (
                <div className="image-preview">
                  {blogImg?.url && (
                    <img
                      src={blogImg.url}
                      alt="Blog banner"
                      onClick={() => setPreviewOpen(true)}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile();
                    }}
                  >
                    <i
                      className="fas fa-trash"
                      style={{ color: "lightgray", pointerEvents: "none" }}
                    ></i>
                  </button>
                </div>
              )}
              <input
                type="file"
                ref={hiddenFileInput}
                accept="image/*"
                onChange={handleImg}
                hidden
              />
            </div>

            <textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              placeholder="Start writing your blog here..."
              className="textarea-blog"
            />

            <div className="blog-action-buttons">
              <button
                className="blog-publish-btn"
                onClick={createBlogPost}
                disabled={loading}
              >
                Publish Blog
              </button>
              <button
                className="blog-cancel-btn"
                onClick={() => navigate("/MyBlogs")}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>

        {console.log("Preview open:", previewOpen, blogImg?.url)}
        {previewOpen && blogImg?.url && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999999,
              padding: "20px", // prevents image from touching edges
            }}
            onClick={() => setPreviewOpen(false)}
          >
            <div
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking image
            >
              <img
                src={blogImg.url}
                alt="Blog Preview"
                style={{
                  maxWidth: "90%",
                  maxHeight: "90%",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                }}
              />
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default CreateBlog;
